<script setup lang="ts">
/**
 * The mail a reply answers, as the OS will quote it below the reply and its signature (#1116): collapsed under the
 * editor behind "Show quoted text", which opens a read-only preview of what is quoted, as its text, with "Remove quoted
 * text" to send this one reply without it. Nothing when the mail has nothing to quote.
 */
import { Button } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import type { MemberEmailDetail } from '@/api'
import { htmlToText } from '@/lib/mailDraft'

const props = defineProps<{
    mail: Pick<MemberEmailDetail, 'fromAddress' | 'fromName' | 'receivedAt' | 'textBody' | 'htmlBody'>
    disabled?: boolean
    testid?: string
}>()
/** Whether the reply quotes the mail. */
const include = defineModel<boolean>({ required: true })
/** Whether the preview is open. Closed until asked for, as mail clients keep it. */
const shown = ref(false)

/** The line the quote opens with, as the OS writes it. */
const attribution = computed(() => {
    const who = props.mail.fromName ? `${props.mail.fromName} <${props.mail.fromAddress}>` : props.mail.fromAddress
    return `On ${new Date(props.mail.receivedAt).toUTCString()}, ${who} wrote:`
})

/** What is quoted, as text: the mail's text part, or its formatted part's text when it arrived with none. */
const quoted = computed(() =>
    props.mail.textBody.trim() === '' ? htmlToText(props.mail.htmlBody ?? '') : props.mail.textBody.trim(),
)

function remove(): void {
    include.value = false
    shown.value = false
}
</script>

<template>
	<div v-if="quoted !== ''" class="space-y-1 text-sm" :data-testid="testid">
		<template v-if="include">
			<Button type="button" size="sm" variant="ghost" :aria-expanded="shown" data-quote-show @click="shown = !shown">
				{{ shown ? 'Hide quoted text' : 'Show quoted text' }}
			</Button>
			<div v-if="shown" class="space-y-1">
				<div class="text-muted-foreground border-border border-l-2 pl-3 text-xs" data-quote-preview>
					<p>{{ attribution }}</p>
					<pre class="font-sans whitespace-pre-wrap">{{ quoted }}</pre>
				</div>
				<Button type="button" size="sm" variant="outline" :disabled="disabled" data-quote-remove @click="remove">
					Remove quoted text
				</Button>
			</div>
		</template>
		<p v-else class="text-muted-foreground flex items-center gap-2 text-xs" data-quote-removed>
			This reply goes without the quoted text.
			<Button type="button" size="sm" variant="ghost" :disabled="disabled" data-quote-restore @click="include = true">
				Quote it again
			</Button>
		</p>
	</div>
</template>
